<template>
  <div class="speaker-fix">
    <p class="speaker-fix__hint">
      Cliquez sur la phrase qui n’est pas de ce locuteur, puis sur ses voisines pour étendre la sélection, et
      choisissez la bonne voix. Le texte ne change pas. Les phrases « à vérifier » sont des répliques courtes au milieu
      du tour, souvent d’une autre personne : écoutez-les.
      <template v-if="!recordingUrl">
        L’écoute n’est plus disponible : l’enregistrement a été effacé, quelques jours après la rédaction des livrables.
      </template>
    </p>

    <ol class="speaker-fix__phrases">
      <li
        v-for="(phrase, i) in phrases"
        :key="phrase.position"
        class="speaker-fix__phrase"
        :class="{ 'speaker-fix__phrase--selected': isSelected(i), 'speaker-fix__phrase--doubtful': phrase.doubtful }"
      >
        <button
          type="button"
          class="speaker-fix__pick"
          :aria-pressed="isSelected(i) ? 'true' : 'false'"
          :disabled="saving"
          @click="toggle(i)"
        >
          <span class="speaker-fix__check" aria-hidden="true"><i class="fas fa-check"></i></span>
          <span class="meeting-speech__speaker" :class="phrase.mixed ? 'meeting-speech__speaker--mixed' : `meeting-speech__speaker--${(phrase.index - 1) % 6}`">
            {{ phrase.name }}
          </span>
          <span v-if="phrase.start !== null" class="speaker-fix__time">{{ formatTimecode(phrase.start) }}</span>
          <span
            v-if="phrase.doubtful"
            class="speaker-fix__doubt"
            title="Réplique courte au milieu de la prise de parole : vérifiez à l’écoute qui parle"
          >à vérifier</span>
          <span class="speaker-fix__text">{{ phrase.text }}</span>
        </button>
        <button
          v-if="recordingUrl"
          type="button"
          class="meetings-outline meetings-outline--small speaker-fix__play"
          :title="playingId === phrase.position ? 'Arrêter l’écoute' : 'Écouter cette phrase'"
          @click="togglePlay(phrase)"
        >
          <i :class="playingId === phrase.position ? 'fas fa-stop' : 'fas fa-play'" aria-hidden="true"></i>
        </button>
      </li>
    </ol>

    <div class="speaker-fix__actions">
      <span class="speaker-fix__count">{{ selectionLabel }}</span>
      <label class="speaker-fix__target">
        Attribuer à
        <select v-model="target" class="speaker-fix__select" :disabled="saving">
          <option value="" disabled>Choisir une voix</option>
          <option v-for="voice in voices" :key="voice.raw" :value="voice.raw" :disabled="voice.name === currentName">
            {{ voice.name }}
          </option>
          <option :value="NEW_VOICE">Nouvelle voix</option>
          <option :value="MIXED_SPEAKER" :disabled="currentName === MIXED_NAME">{{ MIXED_NAME }} (voix qui se chevauchent)</option>
        </select>
      </label>
      <button
        type="button"
        class="meetings-outline meetings-outline--small meetings-outline--primary"
        :disabled="!canSubmit"
        @click="submit"
      >
        <i :class="saving ? 'fas fa-spinner fa-spin' : 'fas fa-exchange-alt'" aria-hidden="true"></i>
        Réattribuer
      </button>
      <button type="button" class="meetings-outline meetings-outline--small" :disabled="saving" @click="$emit('cancel')">
        Annuler
      </button>
    </div>

    <audio
      v-if="recordingUrl"
      ref="player"
      :src="recordingUrl"
      preload="metadata"
      @timeupdate="onTimeUpdate"
      @pause="onPause"
      @ended="onPause"
    ></audio>
  </div>
</template>

<script>
import { MIXED_NAME, MIXED_SPEAKER, doubtfulPositions, formatTimecode, speakerSummary } from './meetingHelpers'
import recordingPlayback, { PLAY_MARGIN_SECONDS } from './recordingPlayback'

/** Valeur envoyée à l'API pour créer une voix qui n'existe pas encore. */
const NEW_VOICE = 'new'

/**
 * Correction du locuteur d'une phrase : une prise de parole (segments `first`
 * à `last` du mot à mot) dépliée en ses phrases, chacune avec sa pastille, son
 * horodatage et son écoute. Le relecteur sélectionne une ou plusieurs phrases
 * consécutives (clic pour choisir, clic sur une voisine pour étendre, sur une
 * extrémité pour réduire), choisit la voix cible parmi celles du mot à mot
 * (nom donné par le relecteur, sinon « Locuteur n ») ou une nouvelle voix, et
 * valide : `move` porte les positions des segments et l'étiquette brute cible ;
 * le parent enregistre et recharge la fiche.
 */
export default {
  name: 'app-meeting-speaker-fix',
  mixins: [recordingPlayback],
  props: {
    meeting: { type: Object, required: true },
    first: { type: Number, required: true },
    last: { type: Number, required: true },
    /** Position d'un segment à présélectionner (la phrase en cause d'une correction). */
    initial: { type: Number, default: null },
    saving: { type: Boolean, default: false }
  },
  data () {
    return {
      selectedFrom: null,
      selectedTo: null,
      target: '',
      NEW_VOICE,
      MIXED_SPEAKER,
      MIXED_NAME
    }
  },
  computed: {
    segments () {
      return Array.isArray(this.meeting.transcript_segments) ? this.meeting.transcript_segments : []
    },
    recordingUrl () {
      return this.meeting.recording_url || ''
    },
    names () {
      return this.meeting.speaker_names && typeof this.meeting.speaker_names === 'object' ? this.meeting.speaker_names : {}
    },
    /**
     * Les voix du mot à mot dans l'ordre d'apparition, désignées par leur nom
     * ou « Locuteur n ». Deux étiquettes qui portent le même nom sont une seule
     * personne (la fiche réunit leurs prises de parole) : une seule entrée, la
     * première apparue.
     */
    voices () {
      const seen = {}
      return speakerSummary(this.segments)
        .slice()
        .sort((a, b) => a.index - b.index)
        .map(voice => ({ raw: voice.raw, index: voice.index, name: this.nameOf(voice.raw, voice.label) }))
        .filter(voice => {
          if (seen[voice.name]) {
            return false
          }
          seen[voice.name] = true
          return true
        })
    },
    /** Les phrases de la prise de parole, sans les segments vides que la fiche n'affiche pas. */
    phrases () {
      // Toutes les étiquettes, y compris celles qui partagent le nom d'une autre (une seule personne) :
      // elles prennent ce nom et la couleur de la première apparue.
      const known = {}
      const colours = {}
      speakerSummary(this.segments).slice().sort((a, b) => a.index - b.index).forEach(voice => {
        const name = this.nameOf(voice.raw, voice.label)
        if (colours[name] === undefined) {
          colours[name] = voice.index
        }
        known[voice.raw] = { index: colours[name], name }
      })
      const doubtful = doubtfulPositions(this.segments, this.first, this.last)
      const phrases = []
      for (let position = this.first; position <= this.last; position++) {
        const segment = this.segments[position]
        const text = segment ? String(segment.text || '').trim() : ''
        if (!text) {
          continue
        }
        const raw = segment.speaker !== null && segment.speaker !== undefined ? String(segment.speaker) : ''
        const mixed = raw === MIXED_SPEAKER
        const voice = mixed ? null : known[raw]
        const index = voice ? voice.index : 0
        phrases.push({
          position,
          raw,
          index,
          mixed,
          name: mixed ? MIXED_NAME : (voice ? voice.name : 'Locuteur ?'),
          start: segment.start !== null && segment.start !== undefined ? Number(segment.start) : null,
          end: segment.end !== null && segment.end !== undefined ? Number(segment.end) : null,
          doubtful: doubtful.includes(position),
          text
        })
      }
      return phrases
    },
    selected () {
      if (this.selectedFrom === null) {
        return []
      }
      return this.phrases.slice(this.selectedFrom, this.selectedTo + 1)
    },
    /** Nom commun aux phrases sélectionnées, pour griser la voix qu'elles ont déjà. */
    currentName () {
      const names = this.selected.map(phrase => phrase.name)
      return names.length && names.every(name => name === names[0]) ? names[0] : null
    },
    targetName () {
      const voice = this.voices.find(candidate => candidate.raw === this.target)
      return voice ? voice.name : null
    },
    selectionLabel () {
      const count = this.selected.length
      if (!count) {
        return 'Aucune phrase sélectionnée'
      }
      return count > 1 ? `${count} phrases sélectionnées` : '1 phrase sélectionnée'
    },
    canSubmit () {
      if (this.saving || !this.selected.length || this.target === '') {
        return false
      }
      if (this.target === NEW_VOICE) {
        return true
      }
      if (this.target === MIXED_SPEAKER) {
        return this.currentName !== MIXED_NAME
      }
      return this.targetName !== this.currentName
    }
  },
  watch: {
    'meeting.uuid' () {
      this.reset()
    }
  },
  created () {
    this.reset()
  },
  methods: {
    formatTimecode,
    nameOf (raw, fallback) {
      const name = this.names[raw] ? String(this.names[raw]).trim() : ''
      return name || fallback
    },
    reset () {
      this.selectedFrom = null
      this.selectedTo = null
      this.target = ''
      if (this.initial !== null) {
        const i = this.phrases.findIndex(phrase => phrase.position === this.initial)
        if (i >= 0) {
          this.selectedFrom = i
          this.selectedTo = i
        }
      }
    },
    isSelected (i) {
      return this.selectedFrom !== null && this.selectedFrom <= i && i <= this.selectedTo
    },
    toggle (i) {
      if (this.selectedFrom === null) {
        this.selectedFrom = i
        this.selectedTo = i
      } else if (i < this.selectedFrom) {
        this.selectedFrom = i
      } else if (i > this.selectedTo) {
        this.selectedTo = i
      } else if (this.selectedFrom === this.selectedTo) {
        this.selectedFrom = null
        this.selectedTo = null
      } else if (i === this.selectedFrom) {
        this.selectedFrom = i + 1
      } else if (i === this.selectedTo) {
        this.selectedTo = i - 1
      } else {
        this.selectedFrom = i
        this.selectedTo = i
      }
    },
    submit () {
      if (!this.canSubmit) {
        return
      }
      this.$emit('move', {
        from: this.phrases[this.selectedFrom].position,
        to: this.phrases[this.selectedTo].position,
        speaker: this.target
      })
    },
    togglePlay (phrase) {
      if (this.playingId === phrase.position) {
        this.stopPlaying()
        return
      }
      const start = Math.max(0, (phrase.start || 0) - PLAY_MARGIN_SECONDS)
      const end = phrase.end !== null ? phrase.end : (phrase.start || 0) + 8
      this.playRange(phrase.position, start, Math.max(end, (phrase.start || 0) + 1) + PLAY_MARGIN_SECONDS)
    }
  }
}
</script>

<style scoped>
.speaker-fix {
  margin-top: 0.35rem;
  padding: 0.75rem 0.9rem;
  border: 1px solid #bfdbfe;
  border-radius: 0.65rem;
  background: #f8fbff;
}

.speaker-fix__hint {
  margin: 0 0 0.6rem;
  color: #4b5563;
  font-size: 0.8rem;
}

.speaker-fix__phrases {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  max-height: 24rem;
  margin: 0;
  padding: 0;
  overflow: auto;
  list-style: none;
}

.speaker-fix__phrase {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
}

.speaker-fix__pick {
  display: flex;
  flex: 1 1 auto;
  align-items: flex-start;
  gap: 0.5rem;
  min-width: 0;
  padding: 0.4rem 0.6rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #ffffff;
  color: #111827;
  font: inherit;
  font-size: 0.875rem;
  line-height: 1.5;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.speaker-fix__pick:hover:not(:disabled) {
  border-color: #93c5fd;
}

.speaker-fix__pick:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.speaker-fix__pick:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.speaker-fix__phrase--selected .speaker-fix__pick {
  border-color: #2563eb;
  background: #dbeafe;
}

.speaker-fix__check {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 1.1rem;
  height: 1.1rem;
  margin-top: 0.1rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #ffffff;
  color: transparent;
  font-size: 0.6rem;
}

.speaker-fix__phrase--selected .speaker-fix__check {
  border-color: #2563eb;
  background: #2563eb;
  color: #ffffff;
}

.speaker-fix__pick .meeting-speech__speaker {
  flex: 0 0 auto;
  margin-top: 0.1rem;
}

.speaker-fix__time {
  flex: 0 0 auto;
  margin-top: 0.15rem;
  color: #9ca3af;
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
}

.speaker-fix__text {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
}

.speaker-fix__doubt {
  flex: 0 0 auto;
  margin-top: 0.1rem;
  padding: 0 0.45rem;
  border-radius: 999px;
  background: #fef3c7;
  color: #92400e;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
}

.speaker-fix__phrase--doubtful .speaker-fix__pick {
  border-color: #f59e0b;
}

.speaker-fix__play {
  flex: 0 0 auto;
  align-self: center;
}

.speaker-fix__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
  margin-top: 0.75rem;
}

.speaker-fix__count {
  margin-right: auto;
  color: #4b5563;
  font-size: 0.8rem;
}

.speaker-fix__target {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: #4b5563;
  font-size: 0.8rem;
}

.speaker-fix__select {
  max-width: 16rem;
  padding: 0.35rem 0.6rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: #ffffff;
  color: #111827;
  font-size: 0.875rem;
}

.speaker-fix__select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

@media (max-width: 768px) {
  .speaker-fix__count {
    flex: 1 1 100%;
    margin-right: 0;
  }
}
</style>
